'use client';

/* Weekly images can be remote uploads, so a plain img is intentional here. */
/* eslint-disable @next/next/no-img-element */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 5;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export default function AndroidWeeklyImageViewer({ image = null, onClose }) {
  const viewportRef = useRef(null);
  const imageRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const transformRef = useRef({ scale: 1, x: 0, y: 0 });
  const closeButtonRef = useRef(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });

  const applyTransform = useCallback((nextTransform) => {
    const viewport = viewportRef.current;
    const target = imageRef.current;
    if (!viewport || !target) return;
    const scale = clamp(nextTransform.scale, MIN_SCALE, MAX_SCALE);
    const limitX = Math.max(0, (target.clientWidth * scale - viewport.clientWidth) / 2);
    const limitY = Math.max(0, (target.clientHeight * scale - viewport.clientHeight) / 2);
    const next = {
      scale,
      x: clamp(nextTransform.x, -limitX, limitX),
      y: clamp(nextTransform.y, -limitY, limitY),
    };
    transformRef.current = next;
    setTransform(next);
  }, []);

  const reset = useCallback(() => applyTransform({ scale: 1, x: 0, y: 0 }), [applyTransform]);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    const marker = { ...window.history.state, weeklyImageViewer: true };
    window.history.pushState(marker, '');
    const handleBack = () => onCloseRef.current?.();
    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, []);

  const closeViewer = useCallback(() => {
    if (window.history.state?.weeklyImageViewer) window.history.back();
    else onCloseRef.current?.();
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeViewer();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', reset);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', reset);
    };
  }, [closeViewer, reset]);

  const beginGesture = useCallback(() => {
    const points = [...pointersRef.current.values()];
    if (points.length === 1) {
      gestureRef.current = { type: 'pan', point: points[0], start: transformRef.current };
      return;
    }
    if (points.length >= 2) {
      const [first, second] = points;
      gestureRef.current = {
        type: 'pinch',
        distance: Math.hypot(second.x - first.x, second.y - first.y) || 1,
        midpoint: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
        start: transformRef.current,
      };
    }
  }, []);

  const handlePointerDown = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    beginGesture();
  }, [beginGesture]);

  const handlePointerMove = useCallback((event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointersRef.current.values()];
    const gesture = gestureRef.current;
    if (!gesture) return;

    if (points.length === 1 && gesture.type === 'pan') {
      applyTransform({
        scale: gesture.start.scale,
        x: gesture.start.x + points[0].x - gesture.point.x,
        y: gesture.start.y + points[0].y - gesture.point.y,
      });
      return;
    }

    if (points.length >= 2 && gesture.type === 'pinch') {
      const [first, second] = points;
      const distance = Math.hypot(second.x - first.x, second.y - first.y) || 1;
      const midpoint = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
      const viewportRect = viewportRef.current?.getBoundingClientRect();
      if (!viewportRect) return;
      const nextScale = clamp(gesture.start.scale * distance / gesture.distance, MIN_SCALE, MAX_SCALE);
      const scaleRatio = nextScale / gesture.start.scale;
      const originX = gesture.midpoint.x - (viewportRect.left + viewportRect.width / 2);
      const originY = gesture.midpoint.y - (viewportRect.top + viewportRect.height / 2);
      applyTransform({
        scale: nextScale,
        x: midpoint.x - gesture.midpoint.x + originX - (originX - gesture.start.x) * scaleRatio,
        y: midpoint.y - gesture.midpoint.y + originY - (originY - gesture.start.y) * scaleRatio,
      });
    }
  }, [applyTransform]);

  const handlePointerEnd = useCallback((event) => {
    pointersRef.current.delete(event.pointerId);
    if (!pointersRef.current.size) {
      gestureRef.current = null;
      if (transformRef.current.scale <= MIN_SCALE) reset();
      return;
    }
    beginGesture();
  }, [beginGesture, reset]);

  const handleDoubleTap = useCallback((event) => {
    if (transformRef.current.scale > 1) {
      reset();
      return;
    }
    const viewportRect = viewportRef.current?.getBoundingClientRect();
    if (!viewportRect) return;
    const originX = event.clientX - (viewportRect.left + viewportRect.width / 2);
    const originY = event.clientY - (viewportRect.top + viewportRect.height / 2);
    applyTransform({ scale: 2.5, x: -originX * 1.5, y: -originY * 1.5 });
  }, [applyTransform, reset]);

  if (!image || typeof document === 'undefined') return null;

  return createPortal(
    <div className="androidWeeklyImageViewer" role="dialog" aria-modal="true" aria-label="Weekly Pictonym picture viewer">
      <button ref={closeButtonRef} type="button" className="androidWeeklyImageViewer__close" onClick={closeViewer} aria-label="Close full-screen picture">
        <X aria-hidden="true" />
      </button>
      <div
        ref={viewportRef}
        className="androidWeeklyImageViewer__viewport"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onDoubleClick={handleDoubleTap}
      >
        <img
          ref={imageRef}
          src={image.src}
          alt={image.alt || 'Weekly Pictonym picture'}
          draggable="false"
          style={{ transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})` }}
        />
      </div>
    </div>,
    document.body,
  );
}
